import React, { memo, useState } from 'react';
import { Editor, Transforms, Element, Node } from 'slate';
import { useSlate } from 'slate-react';
import InlineSvg from 'react-inlinesvg';
import styled from 'styled-components';
import IndentationDecreaseIcon from '../../assets/icon-editor-indentation-decrease.svg';
import IndentationIncreaseIcon from '../../assets/icon-editor-indentation-increase.svg';
import { ToolButtonWrapper, ToolButton } from './ToolBarStyles';
import ToolTip from './ToolTip';
import { ensureSelection } from './utils';

export interface IndentationButtonProps {
  icon: string;
  tooltip: string;
  'data-testid': string;
  $isDisabled?: boolean;
  onToggle: () => void;
}

const IndentationIcon = styled(InlineSvg)<{ $isDisabled: boolean }>`
  width: 26px;
  path {
    fill: ${({ $isDisabled, theme }): string =>
      $isDisabled ? theme.colors.black20Alpha : theme.colors.black};
  }
`;

const IndentationButton = memo(
  ({
    icon,
    tooltip,
    $isDisabled = false,
    onToggle,
    'data-testid': dataTestId,
  }: IndentationButtonProps) => {
    const [tooltipIsVisible, setTooltipIsVisible] = useState<boolean>(false);

    return (
      <ToolButtonWrapper data-testid={dataTestId}>
        <ToolTip text={tooltip} isVisible={tooltipIsVisible}>
          <ToolButton
            onClick={onToggle}
            onMouseEnter={() => {
              setTooltipIsVisible(true);
            }}
            onMouseLeave={() => {
              setTooltipIsVisible(false);
            }}
            disabled={$isDisabled}
          >
            <IndentationIcon src={icon} $isDisabled={$isDisabled} />
          </ToolButton>
        </ToolTip>
      </ToolButtonWrapper>
    );
  }
);

export default (): JSX.Element => {
  const editor: Editor = useSlate();
  const checkIfCanNotDecreaseIndentation = (): boolean => {
    const [match] = Editor.nodes(editor, {
      match: (n: Node) => ((n as Element).type as string) === 'indentation',
    });
    return !editor.selection || !match;
  };
  const checkIfCanNotIncreaseIndentation = (): boolean => {
    return !editor.selection;
  };
  return (
    <>
      <IndentationButton
        tooltip="Decrease Indentation"
        icon={IndentationDecreaseIcon}
        data-testid="IndentationDecreaseButton"
        $isDisabled={checkIfCanNotDecreaseIndentation()}
        onToggle={() => {
          ensureSelection(editor);
          Transforms.unwrapNodes(editor, {
            split: true,
            match: (n: Node) =>
              ((n as Element).type as string) === 'indentation',
          });
        }}
      />
      <IndentationButton
        tooltip="Increase Indentation"
        icon={IndentationIncreaseIcon}
        data-testid="IndentationIncreaseButton"
        $isDisabled={checkIfCanNotIncreaseIndentation()}
        onToggle={() => {
          ensureSelection(editor);
          const block = { type: 'indentation', children: [] };
          Transforms.wrapNodes(editor, block, { split: true });
        }}
      />
    </>
  );
};
